import { cn } from '@/lib/utils';

/**
 * SAB-749: availability marker for a player who is on a roster but NOT available.
 *
 * NO HARDCODED STATUS LIST — deliberately. `nfl_players.status` is written by
 * fetch-nfl-rosters straight from SportRadar's full_roster feed, and the observed
 * vocabulary today is ACT / IR / PUP / NON / EXE / UFA. Enumerating those here would
 * be the FOURTH hardcoded list this session to drift from its source (playoff round
 * names, lineup lock statuses, IDP positions, and nearly this).
 *
 * So the rule is a NEGATIVE one: 'ACT' is available, anything else is not, and the
 * badge renders whatever code the database holds. A status SportRadar adds tomorrow
 * shows up correctly with no code change and no chance of silent divergence.
 *
 * Display only. It never reorders, hides, or alters proj_sab_pts / adp — a player on
 * IR still has draft value depending on how a manager plays it, and sorting him down
 * would be making that call for them.
 */
export function isUnavailable(status: string | null | undefined): boolean {
  return !!status && status.toUpperCase() !== 'ACT';
}

/** Longer-form label, for surfaces with room. Falls back to the raw code. */
const KNOWN_LABELS: Record<string, string> = {
  IR: 'Injured Reserve',
  PUP: 'Physically Unable to Perform',
  NON: 'Non-Football Injury',
  EXE: 'Exempt List',
  UFA: 'Unrestricted Free Agent',
};
export function availabilityLabel(status: string | null | undefined): string {
  if (!status) return '';
  return KNOWN_LABELS[status.toUpperCase()] ?? status.toUpperCase();
}

export function PlayerAvailabilityBadge({
  status, className, size = 'sm',
}: { status: string | null | undefined; className?: string; size?: 'sm' | 'xs' }) {
  if (!isUnavailable(status)) return null;
  const code = (status as string).toUpperCase();
  return (
    <span
      title={availabilityLabel(code)}
      aria-label={`Unavailable: ${availabilityLabel(code)}`}
      className={cn(
        'inline-flex items-center rounded font-bold border whitespace-nowrap',
        'text-destructive border-destructive/50 bg-destructive/10',
        size === 'xs' ? 'px-1 py-0 text-[9px]' : 'px-1.5 py-0 text-[10px]',
        className,
      )}
    >
      {code}
    </span>
  );
}
